import { formatPatientAgeDisplay } from '@/utils/participantDetailDisplay';
import { resolveDefaultSessaoNumero } from './resolveDefaultSessaoNumero';
import type { ProntuarioPatientContext } from './mapMedicalRecordToProntuario';
import {
	buildSummaryFromSession,
	resolveSessionParticipantTarget,
} from './prontuarioPatientSummary';

export type ProntuarioPatientHeader = {
	initials: string;
	fullName: string;
	ageLabel: string;
	sessionNumber: string;
	firstSessionDate: string;
	frequency: string;
	tags: readonly string[];
};

const EMPTY_VALUE = '—';

const buildInitials = (fullName: string): string =>
	fullName
		.split(' ')
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part.charAt(0).toUpperCase())
		.join('') || '?';

const resolveBirthDateFromSession = (
	session?: Sessions.Session | null,
): string | Date | null | undefined => {
	const participant =
		session?.participants?.find(
			(entry) =>
				entry.entity_type === 'PATIENT' || entry.entity_type === 'USER',
		) ?? session?.participants?.[0];

	if (participant?.patient?.birth_date) {
		return participant.patient.birth_date;
	}

	if (participant?.user?.birthday) {
		return participant.user.birthday;
	}

	return null;
};

const resolveBirthDate = (
	session?: Sessions.Session | null,
	patient?: Patients.Patient | null,
	user?: Users.User | null,
): string | Date | null | undefined => {
	if (patient?.birth_date) {
		return patient.birth_date;
	}

	if (user?.birthday) {
		return user.birthday;
	}

	return resolveBirthDateFromSession(session);
};

const resolveAgeLabel = (
	session?: Sessions.Session | null,
	patient?: Patients.Patient | null,
	user?: Users.User | null,
): string => {
	return (
		formatPatientAgeDisplay(resolveBirthDate(session, patient, user)) ??
		EMPTY_VALUE
	);
};

export const resolveProntuarioPatientHeader = (
	session?: Sessions.Session | null,
	patient?: Patients.Patient | null,
	user?: Users.User | null,
	patientContext?: ProntuarioPatientContext,
	recordSessions?: MedicalRecords.MedicalRecordSession[] | null,
): ProntuarioPatientHeader => {
	const ageLabel = resolveAgeLabel(session, patient, user);
	const sessionNumber = resolveDefaultSessaoNumero(session, recordSessions);
	const sharedFields = {
		sessionNumber,
		firstSessionDate: patientContext?.firstSessionDate ?? EMPTY_VALUE,
		frequency: patientContext?.frequency ?? '',
		tags: [] as const,
	};

	if (patient) {
		const fullName = patient.full_name?.trim() || 'Paciente';
		return {
			initials: buildInitials(fullName),
			fullName,
			ageLabel,
			...sharedFields,
		};
	}

	if (user) {
		const fullName =
			[user.first_name, user.last_name]
				.filter(Boolean)
				.join(' ')
				.trim() || 'Paciente';
		return {
			initials: buildInitials(fullName),
			fullName,
			ageLabel,
			...sharedFields,
		};
	}

	const summary = buildSummaryFromSession(session);

	return {
		initials: buildInitials(summary.fullName),
		fullName: summary.fullName,
		ageLabel,
		...sharedFields,
	};
};

export { resolveSessionParticipantTarget };
